import { useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import { Spin } from 'antd';

import citiesData from '@data/cities.json';
import states from '@data/states.json';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { FormBlock } from '@/components/FormBlock';
import { useZustandSync } from '@/hooks/useZustandSync';
import { sendEvent } from '@/lib/ga4';
import { trpc } from '@/lib/trpc';
import { productStore } from '@/stores/productStore';

import { CEPInput } from '../form/CEPInput';
import Checkbox from '../form/Checkbox';
import { Input } from '../form/Input';
import { Select } from '../form/Select';

const producerSchema = z
  .object({
    producerZipCode: z.string().min(8, 'Preencha o CEP para continuar').nonempty('O CEP é obrigatório'),
    producerState: z.string().nonempty('Selecione o estado'),
    producerCity: z.string().nonempty('Selecione a cidade'),
    producerCityId: z.string().nonempty('Selecione a cidade'),
    producerStreetName: z.string().min(5, 'Preencha o nome da rua'),
    producerStreetNumber: z.string().optional(),
    producerDistrict: z.string().min(3, 'Preencha o bairro'),
    producerComplement: z.string().optional(),
    disabledProducerStreetNumber: z.boolean()
  })
  .superRefine((data, ctx) => {
    if (!data.disabledProducerStreetNumber && !data.producerStreetNumber) {
      ctx.addIssue({
        code: 'custom',
        message: 'O número da rua é obrigatório',
        path: ['producerStreetNumber']
      });
    }
  });

type ProducerFormValues = z.infer<typeof producerSchema>;

export default function ProducerAddress() {
  const [showFields, setShowFields] = useState(false);

  const form = useForm<ProducerFormValues>({
    resolver: zodResolver(producerSchema),
    mode: 'onChange',
    defaultValues: {
      producerZipCode: productStore.getState().producer.zipCode || '',
      producerState: productStore.getState().producer.state || '',
      producerCity: productStore.getState().producer.city || '',
      producerCityId: productStore.getState().producer.cityId || '',
      producerStreetName: productStore.getState().producer.streetName || '',
      producerStreetNumber: productStore.getState().producer.streetNumber || '',
      producerDistrict: productStore.getState().producer.district || '',
      producerComplement: productStore.getState().producer.complement || '',
      disabledProducerStreetNumber: productStore.getState().producer.streetNumber === '0'
    }
  });

  const zipCode = useWatch({
    name: 'producerZipCode',
    control: form.control
  });

  const disabledProducerStreetNumber = useWatch({
    name: 'disabledProducerStreetNumber',
    control: form.control
  });

  useZustandSync<ProducerFormValues>(form.control, (data, current) => ({
    producer: {
      ...(current.producer || {}),
      zipCode: data.producerZipCode,
      state: data.producerState,
      city: data.producerCity,
      cityId: data.producerCityId,
      streetName: data.producerStreetName,
      streetNumber: data.producerStreetNumber ?? '',
      district: data.producerDistrict,
      complement: data.producerComplement
    }
  }));

  const cities = useMemo(
    () =>
      citiesData.map(city => ({
        label: city.label,
        value: String(city.value)
      })),
    []
  );

  const enableSearch = useMemo(() => zipCode.length === 8, [zipCode]);

  const { data, error, isLoading } = trpc.addressSearch.searchCEP.useQuery({ cep: zipCode }, { enabled: enableSearch });

  useEffect(() => {
    form.setValue('producerStreetNumber', '0');
  }, [disabledProducerStreetNumber, form]);

  useEffect(() => {
    if (isLoading) {
      setShowFields(false);
    }

    if (error) {
      form.setError('producerZipCode', { type: 'manual', message: 'CEP não encontrado' });
      setShowFields(false);
    }

    if (data) {
      setShowFields(true);
      form.clearErrors();
      form.setValue('producerStreetName', data.street);
      form.setValue('producerDistrict', data.district);
      form.setValue('producerCity', data.city);
      form.setValue('producerCityId', data.cityId);
      form.setValue('producerState', data.state);
    }
  }, [data, error, form, isLoading]);

  return (
    <FormBlock form={form} name='producer-address' title='Endereço'>
      <div className='flex flex-col divide-y divide-black/6'>
        <div className='flex flex-col gap-4'>
          <div>
            <CEPInput name='producerZipCode' suffix={isLoading && <Spin size='small' />} disabled={isLoading} />
          </div>

          <a
            className='text-content-title! w-fit text-sm underline!'
            id='searchCepLink'
            href={'https://buscacepinter.correios.com.br/app/endereco/index.php'}
            target='_blank'
            rel='noopener noreferrer'
            onClick={() => sendEvent('idk_my_cep', 'click')}
          >
            Não sei meu CEP
          </a>

          {showFields && (
            <div className='flex flex-col gap-4'>
              <div className='flex flex-row gap-4'>
                <div className='w-full'>
                  <span className='-mb-1 text-sm font-medium'>Estado</span>
                  <Select id='producerState' name='producerState' values={states} disabled />
                </div>

                <div className='w-full'>
                  <span className='mt-2 -mb-1 text-sm font-medium'>Cidade</span>
                  <Select id='producerCityId' name='producerCityId' values={cities} disabled />
                </div>
              </div>

              <div>
                <span className='-mb-1 text-sm font-medium'>Rua / Avenida</span>
                <Input id='producerStreetName' name='producerStreetName' />
              </div>

              <div>
                <span className='-mb-1 text-sm font-medium'>Número</span>
                <Input
                  id='producerStreetNumber'
                  name='producerStreetNumber'
                  disabled={disabledProducerStreetNumber}
                  suffix={
                    <div className='flex gap-2'>
                      <span>Sem número</span>
                      <Checkbox id='no-number-checkbox' name='disabledProducerStreetNumber' />
                    </div>
                  }
                />
              </div>

              <div>
                <span className='-mb-1 text-sm font-medium'>Bairro</span>
                <Input id='producerDistrict' name='producerDistrict' />
              </div>

              <div>
                <span className='-mb-1 text-sm font-medium'>Complemento (opcional)</span>
                <Input id='producerComplement' name='producerComplement' />
              </div>
            </div>
          )}
        </div>
      </div>
    </FormBlock>
  );
}
